import { Injectable } from '@angular/core';
import { DrupalRESTService } from './drupal-rest.service';
import { AuthService } from './auth.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap, shareReplay, map, distinctUntilChanged } from 'rxjs/operators';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class StudioConfigService {
  private studioConfig = new BehaviorSubject<any>(null);
  private configLoaded = false;
  private currentStudioId: string = '';
  private isLoading = false;

  constructor(
    private drupalRESTService: DrupalRESTService,
    private authService: AuthService
  ) {
    // Subscribe to studio changes
    this.authService.selectedStudioChanged.subscribe((studio) => {
      const newStudioId = studio?.id?.toString() || '';
      if (newStudioId !== this.currentStudioId) {
        LoggerService.log('Studio changed in StudioConfigService, refreshing config:', newStudioId);
        this.currentStudioId = newStudioId;
        this.refreshConfig();
      }
    });
  }

  /**
   * Gets the studio configuration, loading it if necessary
   * Returns an observable that only emits when the configuration actually changes
   */
  getStudioConfig(): Observable<any> {
    if (!this.configLoaded && !this.isLoading && this.hasStudioContext()) {
      this.loadStudioConfig();
    }
    return this.studioConfig.asObservable().pipe(
      distinctUntilChanged((prev, curr) => {
        // Deep comparison for studio config objects
        return JSON.stringify(prev) === JSON.stringify(curr);
      })
    );
  }

  /**
   * Get the current country code from the studio configuration
   */
  getCountryCode(): Observable<string> {
    return this.getStudioConfig().pipe(
      tap(config => {
        if (!config) return null;
      }),
      map(config => config?.['field_studio_address']?.[0]?.country_code || 'US')
    );
  }

  /**
   * Get the explicitly-configured currency code from the studio configuration
   * (the Currency dropdown on the studio information form), or null if unset.
   * Falls through to null so callers can default to country-derived currency.
   */
  getCurrencyCode(): Observable<string | null> {
    return this.getStudioConfig().pipe(
      map(config => config?.['field_currency_code']?.[0]?.value || null)
    );
  }

  /**
   * Refresh the studio configuration (used when studio changes)
   */
  public refreshConfig() {
    this.configLoaded = false;
    this.studioConfig.next(null);
    if (this.hasStudioContext()) {
      this.loadStudioConfig();
    }
  }

  /**
   * Load studio configuration from the API
   */
  private loadStudioConfig() {
    // Prevent concurrent loads
    if (this.isLoading) {
      LoggerService.log('Studio config already loading, skipping duplicate request');
      return;
    }

    if (!this.hasStudioContext()) {
      LoggerService.log('Studio config load skipped until a studio is selected');
      return;
    }
    
    this.isLoading = true;
    const configFieldName = 'field_studio_information_ref';
    this.drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${configFieldName}`)
      .pipe(
        shareReplay(1)
      )
      .subscribe(
        (response) => {
          LoggerService.log('Studio config loaded:', response);
          this.studioConfig.next(response);
          this.configLoaded = true;
          this.isLoading = false;
        },
        (error) => {
          LoggerService.error('Error loading studio configuration:', error);
          this.studioConfig.next(null);
          this.configLoaded = true;
          this.isLoading = false;
        }
      );
  }

  private hasStudioContext(): boolean {
    return !!(
      this.authService.selectedStudio?.id
      || this.authService.studios?.[0]?.id
    );
  }
}
